"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Shared "Ask Amy" pill (item 6): replaces the unlabeled 'A' square from
 * main.html's `.amy-fab` with a captivating but calm pill — visible
 * label, a small sparkle mark, and a soft attention pulse (guarded by
 * `motion-safe:` for reduced-motion users). Used on the public landing
 * header and, styled identically, as the logged-in app shell's Amy
 * launcher (LeftRail button, BottomNav FAB) so "Ask Amy" reads the same
 * way everywhere in the product — one recognizable affordance, the way
 * Google Health's "Ask Coach" pill stays visually constant across its
 * surfaces.
 */

import { forwardRef } from "react"
import { Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"

interface AmyPillButtonProps {
  label: string
  onClick?: () => void
  className?: string
  /** Compact = icon-forward for tight spaces (mobile FAB); still keeps a visible label. */
  compact?: boolean
}

/** forwardRef so this can be used as a Radix `PopoverTrigger`/`DialogTrigger` `asChild` target. */
export const AmyPillButton = forwardRef<HTMLButtonElement, AmyPillButtonProps>(
  function AmyPillButton({ label, onClick, className, compact = false }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          "group relative inline-flex min-h-11 items-center gap-2 rounded-bb-full border border-lime/40 bg-green px-4 text-sm font-semibold text-paper shadow-[0_10px_25px_rgba(29,75,57,0.25)] transition-transform hover:-translate-y-0.5 hover:bg-[#123c2d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral",
          compact && "px-3.5",
          className
        )}
      >
        <span className="relative flex size-6 items-center justify-center rounded-full bg-lime/20">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full motion-safe:animate-[bb-pulse-ring_2.6s_ease-out_infinite]"
          />
          <Sparkles
            aria-hidden="true"
            className="size-3.5 text-lime transition-transform duration-300 group-hover:rotate-12"
          />
        </span>
        <span className={compact ? "sr-only sm:not-sr-only" : undefined}>{label}</span>
      </button>
    )
  }
)
